/**
 * `/admin/conversations/:id` — Story 3.11.
 *
 * Layout:
 *   - Header: customer name + masked phone + status + scheduled meeting
 *   - Sidebar: customer card + meeting card + conversation metadata
 *   - Main: chronological timeline (messages + audit) via
 *     <ConversationTimeline />
 *
 * RBAC: any authenticated role can read. Server-side enforces the actual
 * scoping (see `routes/admin/conversations.ts`); the page does not gate
 * visibility client-side.
 *
 * Data flow:
 *   - React Query fetches `GET /admin/conversations/:id` once on mount.
 *   - A manual "Atualizar" button refetches.
 *   - 404 surfaces a clear empty-state with a back link to `/admin/conversations`.
 */
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  Calendar,
  CalendarPlus,
  Check,
  Clock,
  Copy,
  Lock,
  Pin,
  PinOff,
  RefreshCw,
  Send,
  User,
  UserCheck,
} from 'lucide-react'

import { ApiError, api } from '@/lib/api'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Toast, ToastDescription, ToastTitle } from '@/components/ui/toast'
import { AiAssistantWidget } from '@/components/AiAssistantWidget'
import { ConversationTimeline } from '@/components/ConversationTimeline'
import type {
  AiAssistantPageContext,
  ConversationDetailResponse,
  SellerListResponse,
} from '@sdr-agend/admin-schemas'

interface ToastState {
  open: boolean
  variant: 'default' | 'destructive'
  title: string
  description?: string
}

const TERMINAL_STATUSES = new Set(['closed', 'cancelled', 'declined', 'no_show', 'attended'])

/**
 * Story 10.1 (2026-07-28, user request) — full conversation ID + copy, so an
 * operator can reference this exact conversation when reporting an issue.
 */
function CopyConversationIdInline({ id }: { id: string }): React.ReactElement {
  const [copied, setCopied] = useState(false)

  return (
    <button
      type="button"
      className="inline-flex items-center gap-1 rounded px-1 py-0.5 font-mono text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
      onClick={() => {
        void navigator.clipboard.writeText(id).then(() => {
          setCopied(true)
          setTimeout(() => setCopied(false), 1500)
        })
      }}
      aria-label={`Copiar ID da conversa ${id}`}
    >
      {copied ? (
        <Check className="h-3 w-3 text-emerald-600" aria-hidden="true" />
      ) : (
        <Copy className="h-3 w-3" aria-hidden="true" />
      )}
      {id}
    </button>
  )
}

/**
 * Story 10.2 (2026-07-28, user request) — pin/unpin from the detail page.
 * Backend already exists (`PATCH /admin/conversations/:id/pin`).
 */
function PinToggleButton({
  conversationId,
  pinned,
}: {
  conversationId: string
  pinned: boolean
}): React.ReactElement {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: async (nextPinned: boolean) =>
      api(`/admin/conversations/${conversationId}/pin`, {
        method: 'PATCH',
        body: JSON.stringify({ pinned: nextPinned }),
      }),
    onSuccess: () => {
      // Partial key match — invalidates both this detail query
      // (`['admin-conversations', id]`) and the list page's queries.
      void queryClient.invalidateQueries({ queryKey: ['admin-conversations'] })
    },
  })

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={mutation.isPending}
      onClick={() => mutation.mutate(!pinned)}
      aria-pressed={pinned}
    >
      {pinned ? (
        <Pin className="h-4 w-4 fill-current text-amber-500" aria-hidden="true" />
      ) : (
        <PinOff className="h-4 w-4" aria-hidden="true" />
      )}
      {pinned ? 'Fixada' : 'Fixar'}
    </Button>
  )
}

function formatApiError(err: ApiError): string {
  if (err.bodyJson !== null && typeof err.bodyJson === 'object' && 'error' in err.bodyJson) {
    const e = (err.bodyJson as { error: { code?: string; message?: string } }).error
    return e.message ?? e.code ?? err.message
  }
  return err.message
}

/**
 * Format a (possibly null) ISO date into BR locale `dd/MM/yyyy HH:mm`. Used
 * by header/sidebar cards. Returns an em-dash for `null` so the layout never
 * shows the literal "null".
 */
function fmtDateTime(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso === '') return '—'
  const ms = Date.parse(iso)
  if (Number.isNaN(ms)) return iso
  return new Date(ms).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const STATUS_TONE: Record<
  string,
  'default' | 'secondary' | 'destructive' | 'success' | 'outline'
> = {
  scheduled: 'secondary',
  awaiting_response: 'secondary',
  awaiting_qualification: 'secondary',
  active: 'default',
  confirmed: 'success',
  cancelled: 'destructive',
  rescheduled: 'secondary',
  human_handoff: 'destructive',
  cold: 'outline',
  declined: 'destructive',
  attended: 'success',
  no_show: 'destructive',
  closed: 'outline',
  error: 'destructive',
}

export function ConversationDetailPage(): React.ReactElement {
  const { id } = useParams<{ id?: string }>()

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery<
    ConversationDetailResponse
  >({
    queryKey: ['admin-conversations', id],
    queryFn: async () => api<ConversationDetailResponse>(`/admin/conversations/${id ?? ''}`),
    enabled: id !== undefined && id !== '',
  })

  const queryClient = useQueryClient()
  const [toast, setToast] = useState<ToastState>({ open: false, variant: 'default', title: '' })
  const [draft, setDraft] = useState('')
  const [meetingSellerId, setMeetingSellerId] = useState('')
  const [meetingScheduledAt, setMeetingScheduledAt] = useState('')
  const [meetingDurationMin, setMeetingDurationMin] = useState('60')

  // Story 6.12 — pool de vendedores pro seletor do registro manual de
  // agendamento. Mesma query/endpoint já usado em LeadNewConfigSection.tsx.
  const { data: sellersData } = useQuery<SellerListResponse>({
    queryKey: ['admin-sellers', { active: true }],
    queryFn: async () => api<SellerListResponse>('/admin/sellers?active=true'),
  })
  const sellers = sellersData?.sellers ?? []

  const conversationPageContext: AiAssistantPageContext | null =
    id !== undefined && id !== '' ? { page: 'conversation', conversationId: id } : null

  function notify(state: Omit<ToastState, 'open'>): void {
    setToast({ open: true, ...state })
  }
  function onMutationError(err: Error): void {
    notify({
      variant: 'destructive',
      title: 'Erro',
      description: err instanceof ApiError ? formatApiError(err) : err.message,
    })
  }
  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['admin-conversations', id] })
  }

  const takeoverMutation = useMutation({
    mutationFn: async () =>
      api(`/admin/conversations/${id ?? ''}/handoff/takeover`, { method: 'POST' }),
    onSuccess: () => {
      notify({ variant: 'default', title: 'Conversa assumida — a IA está silenciada.' })
      invalidate()
    },
    onError: onMutationError,
  })
  const releaseMutation = useMutation({
    mutationFn: async () =>
      api(`/admin/conversations/${id ?? ''}/handoff/release`, { method: 'POST' }),
    onSuccess: () => {
      notify({ variant: 'default', title: 'Conversa liberada para a IA.' })
      invalidate()
    },
    onError: onMutationError,
  })
  const closeMutation = useMutation({
    mutationFn: async () => api(`/admin/conversations/${id ?? ''}/close`, { method: 'POST' }),
    onSuccess: () => {
      notify({ variant: 'default', title: 'Conversa fechada.' })
      invalidate()
    },
    onError: onMutationError,
  })
  const sendMutation = useMutation({
    mutationFn: async () =>
      api(`/admin/conversations/${id ?? ''}/messages`, {
        method: 'POST',
        body: JSON.stringify({ text: draft.trim() }),
      }),
    onSuccess: () => {
      setDraft('')
      notify({ variant: 'default', title: 'Mensagem enviada.' })
      invalidate()
    },
    onError: onMutationError,
  })
  const registerMeetingMutation = useMutation({
    mutationFn: async () =>
      api(`/admin/conversations/${id ?? ''}/handoff/meetings`, {
        method: 'POST',
        body: JSON.stringify({
          sellerId: meetingSellerId,
          // QA fix (2026-07-22): <input type="datetime-local"> emite
          // 'YYYY-MM-DDTHH:mm' — SEMPRE os dígitos exatos que o operador
          // digitou, sem nenhuma conversão de fuso (não é o fuso do
          // navegador/SO). A operação assume fuso único América/São Paulo
          // (UTC-3, sem horário de verão desde 2019 — mesma premissa do Epic
          // E9), então o offset correto a anexar é "-03:00", NUNCA "Z"
          // (Conversations.tsx usa "Z" nos filtros de data — mesmo bug,
          // efeito brando lá por ser só filtro de lista; aqui é a hora real
          // do agendamento, e "Z" registraria o compromisso 3h adiantado).
          scheduledAt: `${meetingScheduledAt}:00-03:00`,
          durationMin: Number(meetingDurationMin),
        }),
      }),
    onSuccess: () => {
      setMeetingSellerId('')
      setMeetingScheduledAt('')
      setMeetingDurationMin('60')
      notify({ variant: 'default', title: 'Agendamento registrado.' })
      invalidate()
    },
    onError: onMutationError,
  })

  // 404 from the API → surface a friendly not-found UI with a back link.
  const isNotFound = isError && error instanceof ApiError && error.status === 404

  if (isNotFound) {
    return (
      <div className="space-y-4">
        <Button asChild variant="ghost" size="sm">
          <Link to="/admin/conversations">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Voltar
          </Link>
        </Button>
        <div className="rounded-md border border-dashed p-12 text-center text-sm text-muted-foreground">
          Conversa não encontrada.
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Button asChild variant="ghost" size="sm">
          <Link to="/admin/conversations">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Voltar
          </Link>
        </Button>
        <div className="rounded-md border p-12 text-center text-sm text-muted-foreground">
          Carregando…
        </div>
      </div>
    )
  }

  if (isError || data === undefined) {
    return (
      <div className="space-y-4">
        <Button asChild variant="ghost" size="sm">
          <Link to="/admin/conversations">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Voltar
          </Link>
        </Button>
        <div className="rounded-md border border-destructive p-12 text-center text-sm text-destructive">
          Erro ao carregar a conversa: {(error as Error)?.message ?? 'desconhecido'}
        </div>
      </div>
    )
  }

  const { conversation, customer, seller, meeting, timeline } = data
  const statusTone = STATUS_TONE[conversation.status] ?? 'default'

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" size="sm">
            <Link to="/admin/conversations" aria-label="Voltar para a lista">
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              Voltar
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              {customer.name ?? 'Cliente sem nome'}
            </h1>
            <p className="text-sm text-muted-foreground" data-testid="customer-phone-masked">
              {customer.phoneMasked}
            </p>
            <CopyConversationIdInline id={conversation.id} />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <PinToggleButton conversationId={conversation.id} pinned={conversation.pinnedAt !== null} />
          {conversation.campaignName !== null && conversation.campaignName !== undefined && (
            <Badge variant="outline">{conversation.campaignName}</Badge>
          )}
          <Badge variant={statusTone}>{conversation.status}</Badge>
          {conversation.pendingCallbackAt !== null && (
            <Badge
              variant="secondary"
              className="inline-flex items-center gap-1"
              data-testid="pending-callback-badge"
            >
              <Clock className="h-3 w-3" aria-hidden="true" />
              Recontato agendado: {fmtDateTime(conversation.pendingCallbackAt)}
            </Badge>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void refetch()}
            disabled={isFetching}
            aria-label="Atualizar conversa"
          >
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            Atualizar
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <section aria-label="Timeline da conversa" className="min-w-0 space-y-4">
          <ConversationTimeline entries={timeline} />
        </section>

        <aside className="space-y-4" aria-label="Detalhes da conversa">
          <div className="rounded-md border p-4">
            <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold">
              <User className="h-4 w-4" aria-hidden="true" /> Cliente
            </h2>
            <dl className="space-y-1 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">Nome</dt>
                <dd>{customer.name ?? '—'}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">Telefone</dt>
                <dd>{customer.phoneMasked}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">Email</dt>
                <dd className="truncate">{customer.email ?? '—'}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">Opt-out</dt>
                <dd>{customer.optedOut ? 'Sim' : 'Não'}</dd>
              </div>
            </dl>
          </div>

          <div className="rounded-md border p-4">
            <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold">
              <Calendar className="h-4 w-4" aria-hidden="true" /> Reunião
            </h2>
            {meeting === null ? (
              <p className="text-sm text-muted-foreground">Sem reunião vinculada.</p>
            ) : (
              <dl className="space-y-1 text-sm">
                <div className="flex justify-between gap-3">
                  <dt className="text-muted-foreground">Vendedor</dt>
                  <dd>{seller?.name ?? '—'}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-muted-foreground">Agendada para</dt>
                  <dd>{fmtDateTime(meeting.scheduledAt)}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-muted-foreground">Duração</dt>
                  <dd>{meeting.durationMin} min</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-muted-foreground">Origem</dt>
                  <dd>{meeting.source}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-muted-foreground">Status</dt>
                  <dd>{meeting.outcome}</dd>
                </div>
              </dl>
            )}
          </div>

          <div className="rounded-md border p-4">
            <h2 className="mb-3 text-sm font-semibold">Conversa</h2>
            <dl className="space-y-1 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">Última mensagem</dt>
                <dd>{fmtDateTime(conversation.lastMessageAt)}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">Último intent</dt>
                <dd>{conversation.lastInboundIntent ?? '—'}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">Sentimento</dt>
                <dd>{conversation.lastInboundSentiment ?? '—'}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">Tentativas de reativação</dt>
                <dd>{conversation.reactivationAttempts}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">Reagendamentos</dt>
                <dd>{conversation.rescheduleCount}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">Aberta em</dt>
                <dd>{fmtDateTime(conversation.createdAt)}</dd>
              </div>
            </dl>
          </div>

          <div className="rounded-md border p-4">
            <h2 className="mb-3 text-sm font-semibold">Ações do operador</h2>
            {TERMINAL_STATUSES.has(conversation.status) ? (
              <p className="text-sm text-muted-foreground">
                Conversa encerrada — sem ações disponíveis.
              </p>
            ) : (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => takeoverMutation.mutate()}
                    disabled={takeoverMutation.isPending}
                  >
                    <UserCheck className="h-4 w-4" aria-hidden="true" /> Assumir
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => releaseMutation.mutate()}
                    disabled={releaseMutation.isPending}
                  >
                    Liberar p/ IA
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    onClick={() => {
                      if (window.confirm('Fechar esta conversa? Ela sairá do fluxo ativo.')) {
                        closeMutation.mutate()
                      }
                    }}
                    disabled={closeMutation.isPending}
                  >
                    <Lock className="h-4 w-4" aria-hidden="true" /> Fechar
                  </Button>
                </div>
                <div className="space-y-2">
                  <Textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="Responder ao cliente (assuma a conversa antes de enviar)…"
                    rows={3}
                    aria-label="Mensagem manual ao cliente"
                  />
                  <Button
                    type="button"
                    size="sm"
                    className="w-full"
                    onClick={() => sendMutation.mutate()}
                    disabled={sendMutation.isPending || draft.trim().length === 0}
                  >
                    <Send className="h-4 w-4" aria-hidden="true" /> Enviar
                  </Button>
                </div>
                {conversation.agentSilenced && (
                  <div className="space-y-2 border-t pt-3" aria-label="Registrar agendamento manual">
                    <h3 className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
                      <CalendarPlus className="h-3.5 w-3.5" aria-hidden="true" />
                      Registrar agendamento fechado por telefone
                    </h3>
                    <div className="space-y-1">
                      <Label htmlFor="meeting-seller">Vendedor</Label>
                      <Select
                        id="meeting-seller"
                        value={meetingSellerId}
                        onChange={(e) => setMeetingSellerId(e.target.value)}
                      >
                        <option value="">— selecione —</option>
                        {sellers.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label htmlFor="meeting-scheduled-at">Data/hora</Label>
                        <Input
                          id="meeting-scheduled-at"
                          type="datetime-local"
                          value={meetingScheduledAt}
                          onChange={(e) => setMeetingScheduledAt(e.target.value)}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="meeting-duration">Duração (min)</Label>
                        <Input
                          id="meeting-duration"
                          type="number"
                          min={1}
                          max={480}
                          value={meetingDurationMin}
                          onChange={(e) => setMeetingDurationMin(e.target.value)}
                        />
                      </div>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      className="w-full"
                      onClick={() => registerMeetingMutation.mutate()}
                      disabled={
                        registerMeetingMutation.isPending ||
                        meetingSellerId === '' ||
                        meetingScheduledAt === ''
                      }
                    >
                      <CalendarPlus className="h-4 w-4" aria-hidden="true" /> Registrar agendamento
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </aside>
      </div>

      <Toast
        open={toast.open}
        onOpenChange={(open) => setToast((s) => ({ ...s, open }))}
        variant={toast.variant}
      >
        <div className="grid gap-1">
          <ToastTitle>{toast.title}</ToastTitle>
          {toast.description !== undefined && (
            <ToastDescription>{toast.description}</ToastDescription>
          )}
        </div>
      </Toast>

      <AiAssistantWidget key={id} pageContext={conversationPageContext} />
    </div>
  )
}

export default ConversationDetailPage
